import React from "react";
import Div from "@jumbo/shared/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { WRISTBAND_TEMPALTE_TYPES } from "app/utils/constants/settings";
import ProductZoneAccessSection from "./ProductZoneAccessSection";

const InfoHelpBox = ({ title, items }) => (
  <Alert severity="info" variant="outlined">
    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
      {items.map(({ key, text, children }) => (
        <Box component="li" key={key}>
          <Typography variant="body2">{text}</Typography>
          {children?.length ? (
            <Box component="ul" sx={{ mt: 0.25, pl: 2.5 }}>
              {children.map((child) => (
                <Box component="li" key={child.key}>
                  <Typography variant="body2">{child.text}</Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      ))}
    </Box>
  </Alert>
);

const IndividualSettingsTab = ({
  values,
  setFieldValue,
  t,
  ticketPrintersGrouped,
  ticketPrintersLoading = false,
}) => {
  /** Template ID applies only to wristband printers (`accessTicket`), not plain ticket printers. */
  const hasWristbandPrintingSelected = React.useMemo(() => {
    const ids = Array.isArray(values?.ticketSettings?.printerIds)
      ? values.ticketSettings.printerIds
          .map((id) => String(id).trim())
          .filter(Boolean)
      : [];
    if (ids.length === 0 || !Array.isArray(ticketPrintersGrouped)) {
      return false;
    }
    const idSet = new Set(ids);
    for (const g of ticketPrintersGrouped) {
      for (const p of g.printers || []) {
        if (String(p?.type || "") !== "accessTicket") continue;
        const pu = p.uuid != null ? String(p.uuid).trim() : "";
        const pid = p.id != null ? String(p.id).trim() : "";
        if (pu && idSet.has(pu)) return true;
        if (pid && idSet.has(pid)) return true;
      }
    }
    return false;
  }, [values?.ticketSettings?.printerIds, ticketPrintersGrouped]);
  const currentTemplateId =
    Number(values?.ticketSettings?.templateId) === 1 ||
    Number(values?.ticketSettings?.templateId) === 2
      ? String(Number(values?.ticketSettings?.templateId))
      : "";

  const ticketType = values?.ticketSettings?.validityType || "";
  const isBackendManagedType =
    ticketType === "time_range" || ticketType === "entry_count";

  const ticketValidityHelpItems = [
    {
      key: "calendarDay",
      text: t("pages.dsokProducts.ticketValidity.help.point1", {
        defaultValue: "Platí pre jeden kalendárny deň.",
      }),
    },
    {
      key: "validDayOnly",
      text: t("pages.dsokProducts.ticketValidity.help.point2", {
        defaultValue: "Lístok je možné použiť iba v deň jeho platnosti.",
      }),
    },
    {
      key: "backendCheck",
      text: t("pages.dsokProducts.ticketValidity.help.point3", {
        defaultValue:
          "Backend automaticky kontroluje platnosť pri každom použití lístka.",
      }),
    },
    {
      key: "expiredAfterDay",
      text: t("pages.dsokProducts.ticketValidity.help.point4", {
        defaultValue:
          "Po skončení dňa sa lístok automaticky považuje za neplatný.",
      }),
    },
  ];

  const wristbandPrintHelpItems = [
    {
      key: "templateChoice",
      text: t("pages.dsokProducts.ticketTemplateId.helpBox.point1", {
        defaultValue:
          "Ak je vybraná tlačiareň náramkov, môžete zvoliť spôsob tlače pomocou Template ID.",
      }),
    },
    {
      key: "templateStandard",
      text: t("pages.dsokProducts.ticketTemplateId.helpBox.template1Title", {
        defaultValue: "Template 1 (štandard):",
      }),
      children: [
        {
          key: "templateStandardDescription",
          text: t(
            "pages.dsokProducts.ticketTemplateId.helpBox.template1Description",
            {
              defaultValue: "tlač je čierna na svetlý náramok.",
            },
          ),
        },
      ],
    },
    {
      key: "templateInverse",
      text: t("pages.dsokProducts.ticketTemplateId.helpBox.template2Title", {
        defaultValue: "Template 2 (inverzný):",
      }),
      children: [
        {
          key: "templateInverseDescription",
          text: t(
            "pages.dsokProducts.ticketTemplateId.helpBox.template2Description",
            {
              defaultValue:
                "podklad náramku je čierny a tlač je biela (alebo farba náramku).",
            },
          ),
        },
      ],
    },
    {
      key: "visualOnly",
      text: t("pages.dsokProducts.ticketTemplateId.helpBox.point4", {
        defaultValue:
          "Výber template ovplyvňuje vzhľad vytlačeného náramku, nie jeho funkcionalitu.",
      }),
    },
  ];


  // Clear unused fields when switching validity type
  React.useEffect(() => {
    if (values?.productType !== "ticket") return;
    const type = values?.ticketSettings?.validityType;
    if (!type) return;
    if (type === "time_range" || type === "entry_count") return;

    if (type !== "from_first_use") {
      setFieldValue("ticketSettings.durationMinutes", null);
    }
    if (type !== "calendar_day") {
      setFieldValue("ticketSettings.timeFrom", "00:00");
      setFieldValue("ticketSettings.timeTo", "23:59");
    }
    if (type === "calendar_day") {
      if (!values?.ticketSettings?.timeFrom) {
        setFieldValue("ticketSettings.timeFrom", "00:00");
      }
      if (!values?.ticketSettings?.timeTo) {
        setFieldValue("ticketSettings.timeTo", "23:59");
      }
    }
  }, [
    setFieldValue,
    values?.productType,
    values?.ticketSettings?.validityType,
  ]);

  // Clear ticket settings when switching to standard product
  React.useEffect(() => {
    if (values?.productType === "ticket") return;
    if (!values?.ticketSettings) return;
    setFieldValue("ticketSettings.validityType", "");
    setFieldValue("ticketSettings.validFrom", null);
    setFieldValue("ticketSettings.validTo", null);
    setFieldValue("ticketSettings.durationMinutes", null);
    setFieldValue("ticketSettings.maxEntries", null);
    setFieldValue("zoneAccess", {
      zones: [],
      allowTurnstile: true,
      allowGate: true,
      deviceOverrides: [],
    });
  }, [setFieldValue, values?.productType]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4} lg={3}>
        <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
          <JumboTextField
            select
            fullWidth
            name="productType"
            label={t("pages.dsokProducts.productType.title")}
          >
            <MenuItem value="standard">
              {t("pages.dsokProducts.productType.standard")}
            </MenuItem>
            <MenuItem value="ticket">
              {t("pages.dsokProducts.productType.ticket")}
            </MenuItem>
          </JumboTextField>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            {t("pages.dsokProducts.productType.help")}
          </Typography>
        </Div>
      </Grid>
      <Grid item xs={12} md={12} lg={12}></Grid>

      {values?.productType === "ticket" ? (
        <>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                mt: 1,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                height: "100%",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                {t("pages.dsokProducts.ticketValidity.title", {
                  defaultValue: "Platnosť lístka",
                })}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Div sx={{ mx: 0 }}>
                    <JumboTextField
                      select
                      fullWidth
                      name="ticketSettings.validityType"
                      label={t("pages.dsokProducts.ticketValidity.typeLabel", {
                        defaultValue: "Typ platnosti",
                      })}
                      disabled={isBackendManagedType}
                    >
                      <MenuItem value="single_entry">
                        {t(
                          "pages.dsokProducts.ticketValidity.types.single_entry",
                          {
                            defaultValue: "Jednorazový vstup",
                          },
                        )}
                      </MenuItem>
                      <MenuItem value="from_first_use">
                        {t(
                          "pages.dsokProducts.ticketValidity.types.from_first_use",
                          { defaultValue: "Platnosť od prvého použitia" },
                        )}
                      </MenuItem>
                      <MenuItem value="calendar_day">
                        {t(
                          "pages.dsokProducts.ticketValidity.types.calendar_day",
                          {
                            defaultValue: "Kalendárny deň",
                          },
                        )}
                      </MenuItem>
                      {isBackendManagedType ? (
                        <MenuItem value={ticketType} sx={{ display: "none" }}>
                          {ticketType}
                        </MenuItem>
                      ) : null}
                    </JumboTextField>
                  </Div>
                </Grid>

                <Grid item xs={12}>
                  <InfoHelpBox
                    title={t("pages.dsokProducts.ticketValidity.help.title", {
                      defaultValue: "Ako funguje platnosť",
                    })}
                    items={ticketValidityHelpItems}
                  />
                </Grid>

                {ticketType === "from_first_use" ? (
                  <Grid item xs={12}>
                    <Div sx={{ mx: 0 }}>
                      <JumboTextField
                        type="number"
                        fullWidth
                        name="ticketSettings.durationMinutes"
                        label={t(
                          "pages.dsokProducts.ticketValidity.durationMinutes",
                          {
                            defaultValue: "Trvanie (minúty)",
                          },
                        )}
                        InputProps={{
                          inputProps: { min: 1, step: 1 },
                          onChange: (e) => {
                            const raw = e?.target?.value;
                            const next =
                              raw === "" || raw === null || raw === undefined
                                ? null
                                : Number.parseInt(String(raw), 10);
                            setFieldValue(
                              "ticketSettings.durationMinutes",
                              Number.isFinite(next) ? next : null,
                            );
                          },
                        }}
                      />
                    </Div>
                  </Grid>
                ) : null}

                {ticketType === "single_entry" ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        "pages.dsokProducts.ticketValidity.info.single_entry",
                        {
                          defaultValue:
                            "Jednorazový vstup bez ďalších nastavení.",
                        },
                      )}
                    </Typography>
                  </Grid>
                ) : null}

                {ticketType === "calendar_day" ? (
                  <>
                    <Grid item xs={6}>
                      <Div sx={{ mx: 0 }}>
                        <JumboTextField
                          fullWidth
                          type="time"
                          name="ticketSettings.timeFrom"
                          label={t(
                            "pages.dsokProducts.ticketValidity.timeFrom",
                            { defaultValue: "Platný od" },
                          )}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 60 }}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={6}>
                      <Div sx={{ mx: 0 }}>
                        <JumboTextField
                          fullWidth
                          type="time"
                          name="ticketSettings.timeTo"
                          label={t(
                            "pages.dsokProducts.ticketValidity.timeTo",
                            { defaultValue: "Platný do" },
                          )}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 60 }}
                        />
                      </Div>
                    </Grid>
                  </>
                ) : null}
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                mt: 1,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                height: "100%",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                {t("pages.dsokProducts.ticketPrinters.title", {
                  defaultValue: "Tlačiarne pre lístky",
                })}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <FormGroup sx={{ gap: 0.5 }}>
                {ticketPrintersLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 2,
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <>
                    {(!ticketPrintersGrouped ||
                      ticketPrintersGrouped.length === 0) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 0.5 }}
                      >
                        {t("pages.dsokProducts.ticketPrinters.none", {
                          defaultValue:
                            "Nie sú k dispozícii žiadne tlačiarne pre lístky.",
                        })}
                      </Typography>
                    )}
                    {ticketPrintersGrouped &&
                      ticketPrintersGrouped.map(
                        ({ workspaceKey, workspaceName, printers }) => {
                          const printerIds =
                            Array.isArray(values?.ticketSettings?.printerIds) &&
                            values.ticketSettings.printerIds
                              .map((id) => String(id))
                              .filter((id) => id.trim() !== "");

                          const handleChange = (printerUuid, checked) => {
                            const current = Array.isArray(
                              values?.ticketSettings?.printerIds,
                            )
                              ? values.ticketSettings.printerIds.map((id) =>
                                  String(id),
                                )
                              : [];
                            let next;
                            if (checked) {
                              next = Array.from(
                                new Set([...current, String(printerUuid)]),
                              );
                            } else {
                              next = current.filter(
                                (id) => id !== String(printerUuid),
                              );
                            }
                            setFieldValue("ticketSettings.printerIds", next);
                          };

                          return (
                            <Box key={workspaceKey} sx={{ mt: 1 }}>
                              <Typography
                                variant="caption"
                                color="gray"
                                sx={{ display: "block", mb: 0.5 }}
                              >
                                {workspaceName}
                              </Typography>
                              {printers.map(({ uuid, name }) => (
                                <FormControlLabel
                                  key={uuid || `${workspaceKey}-${name}`}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={
                                        Array.isArray(printerIds) &&
                                        uuid &&
                                        printerIds.includes(String(uuid))
                                      }
                                      onChange={(e) =>
                                        handleChange(uuid, e.target.checked)
                                      }
                                    />
                                  }
                                  label={name || uuid}
                                />
                              ))}
                            </Box>
                          );
                        },
                      )}
                  </>
                )}
              </FormGroup>

              {hasWristbandPrintingSelected ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {t("pages.dsokProducts.ticketTemplateId.title", {
                      defaultValue: "Template ID",
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("pages.dsokProducts.ticketTemplateId.help", {
                      defaultValue:
                        "Vyberte šablónu tlače náramku pre zvolenú tlačiareň.",
                    })}
                  </Typography>
                  <RadioGroup
                    value={currentTemplateId}
                    onChange={(e) => {
                      const next = Number.parseInt(e?.target?.value, 10);
                      setFieldValue(
                        "ticketSettings.templateId",
                        Number.isFinite(next) ? next : null,
                      );
                    }}
                    sx={{ mt: 0.5 }}
                  >
                    {WRISTBAND_TEMPALTE_TYPES.map((type) => (
                      <FormControlLabel
                        key={type.code}
                        value={String(type.code)}
                        control={<Radio size="small" />}
                        label={`${type.code} - ${t(
                          `pages.dsokProducts.ticketTemplateId.options.${type.company}`,
                          { defaultValue: type.company },
                        )}`}
                      />
                    ))}
                  </RadioGroup>
                  <Box sx={{ mt: 1.5 }}>
                    <InfoHelpBox
                      title={t(
                        "pages.dsokProducts.ticketTemplateId.helpBox.title",
                        {
                          defaultValue: "Ako funguje tlač náramkov",
                        },
                      )}
                      items={wristbandPrintHelpItems}
                    />
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <ProductZoneAccessSection
              values={values}
              setFieldValue={setFieldValue}
              t={t}
            />
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};

export default IndividualSettingsTab;
